import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
    startRegistration,
    startAuthentication,
} from '@simplewebauthn/browser';

const BaseURL = process.env.REACT_APP_API_URL;

const getAuthHeaders = () => ({
    token: Cookies.get('authToken'),
    'Content-Type': 'application/json',
});

// ─── REGISTER ──────────────────────────────────────────────────────────────────

export const RegisterFingerprint = createAsyncThunk(
    'WebAuthn/RegisterFingerprint',
    async (deviceName, { rejectWithValue }) => {
        try {
            // Step 1: Get registration options from server
            const optionsRes = await axios.post(
                `${BaseURL}/WebAuthn/register-options`,
                {},
                { headers: getAuthHeaders() }
            );
            const options = optionsRes.data.options;

            // Step 2: Browser prompts fingerprint scan and returns credential
            const credential = await startRegistration(options);

            // Step 3: Send credential to server for verification + storage
            const verifyRes = await axios.post(
                `${BaseURL}/WebAuthn/register-verify`,
                { credential, deviceName: deviceName || 'My Device' },
                { headers: getAuthHeaders() }
            );

            return verifyRes.data;
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                return rejectWithValue('Fingerprint scan was cancelled or not allowed.');
            }
            if (error.name === 'InvalidStateError') {
                return rejectWithValue('This device is already registered.');
            }
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Registration failed'
            );
        }
    }
);

export const GetMyDevices = createAsyncThunk(
    'WebAuthn/GetMyDevices',
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${BaseURL}/WebAuthn/my-devices`, {
                headers: getAuthHeaders(),
            });
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const DeleteDevice = createAsyncThunk(
    'WebAuthn/DeleteDevice',
    async (credentialId, { rejectWithValue, dispatch }) => {
        try {
            const res = await axios.post(
                `${BaseURL}/WebAuthn/delete-device/${credentialId}`,
                {},
                { headers: getAuthHeaders() }
            );
            dispatch(GetMyDevices());
            return res.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// ─── LOGIN ─────────────────────────────────────────────────────────────────────

export const LoginWithFingerprint = createAsyncThunk(
    'WebAuthn/LoginWithFingerprint',
    async (User_ID, { rejectWithValue }) => {
        try {
            // Step 1: Get authentication options
            const optionsRes = await axios.post(`${BaseURL}/WebAuthn/login-options`, { User_ID });
            const options = optionsRes.data.options;

            // Step 2: Browser prompts fingerprint and returns assertion
            const credential = await startAuthentication(options);

            // Step 3: Verify and get JWT token
            const verifyRes = await axios.post(`${BaseURL}/WebAuthn/login-verify`, {
                User_ID,
                credential,
            });

            return verifyRes.data;
        } catch (error) {
            if (error.name === 'NotAllowedError') {
                return rejectWithValue('Fingerprint scan was cancelled.');
            }
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Fingerprint login failed'
            );
        }
    }
);

// ─── SLICE ─────────────────────────────────────────────────────────────────────

const webAuthnSlice = createSlice({
    name: 'WebAuthn',
    
    initialState: {
        devices: [],
        devicesLoading: false,
        registerLoading: false,
        loginLoading: false,
        loginData: null,
        error: null,
        successMessage: null,
    },

    reducers: {
        clearWebAuthnMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        },
        clearLoginData: (state) => {
            state.loginData = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // RegisterFingerprint
            .addCase(RegisterFingerprint.pending, (state) => {
                state.registerLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(RegisterFingerprint.fulfilled, (state, action) => {
                state.registerLoading = false;
                state.successMessage = action.payload.message || 'Fingerprint registered!';
            })
            .addCase(RegisterFingerprint.rejected, (state, action) => {
                state.registerLoading = false;
                state.error = action.payload;
            })

            // GetMyDevices
            .addCase(GetMyDevices.pending, (state) => {
                state.devicesLoading = true;
            })
            .addCase(GetMyDevices.fulfilled, (state, action) => {
                state.devicesLoading = false;
                state.devices = action.payload.data || [];
            })
            .addCase(GetMyDevices.rejected, (state, action) => {
                state.devicesLoading = false;
                state.error = action.payload;
            })

            // DeleteDevice
            .addCase(DeleteDevice.pending, (state) => {
                state.devicesLoading = true;
            })
            .addCase(DeleteDevice.fulfilled, (state) => {
                state.devicesLoading = false;
                state.successMessage = 'Device removed';
            })
            .addCase(DeleteDevice.rejected, (state, action) => {
                state.devicesLoading = false;
                state.error = action.payload;
            })

            // LoginWithFingerprint
            .addCase(LoginWithFingerprint.pending, (state) => {
                state.loginLoading = true;
                state.error = null;
                state.loginData = null;
            })
            .addCase(LoginWithFingerprint.fulfilled, (state, action) => {
                state.loginLoading = false;
                state.loginData = action.payload;
            })
            .addCase(LoginWithFingerprint.rejected, (state, action) => {
                state.loginLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearWebAuthnMessages, clearLoginData } = webAuthnSlice.actions;
export default webAuthnSlice.reducer;
